import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Copy } from 'lucide-react';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FormField } from '@/components/shared/form-field';
import { Badge, type BadgeVariant } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/toast';
import { useIsPlatformAdmin } from '@/hooks/use-is-platform-admin';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { formatDateTime } from '@/utils/format';

export interface ScheduledJobRow {
  name: string;
  everyMs: number;
  scope: 'global' | 'church_local';
  lastRun: {
    startedAt: string;
    finishedAt: string | null;
    outcome: string;
    itemsProcessed: number;
    itemsFailed: number;
  } | null;
}

export interface JobRunRow {
  id: string;
  jobName: string;
  startedAt: string;
  finishedAt: string | null;
  outcome: string;
  itemsProcessed: number;
  itemsFailed: number;
  attempt: number;
}

export interface DeadLetterRow {
  id: string;
  jobName: string;
  churchId: string | null;
  attempts: number;
  error: string;
  resolvedAt: string | null;
  createdAt: string;
}

interface JobRunDetail extends JobRunRow {
  error: string | null;
  details: unknown;
}

interface DeadLetterDetail extends DeadLetterRow {
  payload: unknown;
}

const outcomeBadgeVariant = (outcome: string): BadgeVariant => {
  if (outcome === 'succeeded') return 'success';
  if (outcome === 'partial') return 'warning';
  if (outcome === 'failed' || outcome === 'dead_lettered') return 'danger';
  if (outcome === 'running') return 'info';
  return 'neutral';
};

const DetailRow = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="grid gap-1 border-b border-border py-3 last:border-b-0 sm:grid-cols-[9rem_1fr] sm:gap-4">
    <dt className="text-body-sm text-fg-secondary">{label}</dt>
    <dd className="min-w-0 text-body text-fg">{children}</dd>
  </div>
);

const CopyableText = ({
  value,
  copyLabel,
  copiedLabel,
  copyFailedLabel,
  monospace = false,
}: {
  value: string;
  copyLabel: string;
  copiedLabel: string;
  copyFailedLabel: string;
  monospace?: boolean;
}) => {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success(copiedLabel);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(copyFailedLabel);
    }
  };

  return (
    <div className="flex items-start gap-2">
      <pre
        className={`min-w-0 flex-1 whitespace-pre-wrap break-words text-body-sm ${
          monospace ? 'font-mono' : ''
        }`}
      >
        {value}
      </pre>
      <Button
        type="button"
        variant="secondary"
        size="sm"
        className="shrink-0"
        aria-label={copyLabel}
        onClick={() => void copy()}
      >
        {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
      </Button>
    </div>
  );
};

const formatJson = (value: unknown): string | null => {
  if (value == null) return null;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

const formatInterval = (
  everyMs: number,
  t: (key: string, options?: Record<string, unknown>) => string,
): string => {
  const hourMs = 60 * 60 * 1000;
  const minuteMs = 60 * 1000;
  if (everyMs % hourMs === 0) {
    return t('operations.intervals.hours', { count: everyMs / hourMs });
  }
  if (everyMs % minuteMs === 0) {
    return t('operations.intervals.minutes', { count: everyMs / minuteMs });
  }
  return t('operations.intervals.seconds', { count: Math.round(everyMs / 1000) });
};

const useJobLabel = () => {
  const { t } = useTranslation('admin');
  return (name: string) => t(`operations.jobNames.${name}`, { defaultValue: name });
};

export const ScheduledJobDetailDialog = ({
  job,
  schedulerEnabled,
  open,
  onOpenChange,
}: {
  job: ScheduledJobRow | null;
  schedulerEnabled: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const { t } = useTranslation('admin');
  const { t: tCommon } = useTranslation('common');
  const jobLabel = useJobLabel();

  const descriptionKey = job ? `operations.jobDescriptions.${job.name}` : null;
  const description =
    descriptionKey != null
      ? t(descriptionKey, { defaultValue: '' })
      : '';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        size="md"
        className="overflow-visible sm:max-w-xl"
        closeLabel={tCommon('action.close')}
      >
        <DialogHeader>
          <DialogTitle>
            {job ? jobLabel(job.name) : t('operations.detail.scheduledTitleFallback')}
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          {job ? (
            <dl>
              <DetailRow label={t('operations.columns.job')}>
                <div>
                  <p className="font-medium">{jobLabel(job.name)}</p>
                  <p className="text-caption text-fg-secondary">{job.name}</p>
                </div>
              </DetailRow>
              {description ? (
                <DetailRow label={t('operations.detail.description')}>{description}</DetailRow>
              ) : null}
              <DetailRow label={t('operations.columns.schedule')}>
                {formatInterval(job.everyMs, t)}
              </DetailRow>
              <DetailRow label={t('operations.columns.scope')}>
                {t(`operations.scopes.${job.scope}`)}
              </DetailRow>
              <DetailRow label={t('operations.detail.scheduler')}>
                <Badge variant={schedulerEnabled ? 'success' : 'warning'}>
                  {schedulerEnabled
                    ? t('operations.stats.schedulerOn')
                    : t('operations.stats.schedulerOff')}
                </Badge>
              </DetailRow>
              <DetailRow label={t('operations.columns.lastRun')}>
                {job.lastRun ? formatDateTime(job.lastRun.startedAt) : t('operations.detail.noRunYet')}
              </DetailRow>
              {job.lastRun ? (
                <>
                  <DetailRow label={t('operations.detail.finishedAt')}>
                    {job.lastRun.finishedAt
                      ? formatDateTime(job.lastRun.finishedAt)
                      : t('operations.detail.stillRunning')}
                  </DetailRow>
                  <DetailRow label={t('operations.columns.outcome')}>
                    <Badge variant={outcomeBadgeVariant(job.lastRun.outcome)}>
                      {t(`operations.outcomes.${job.lastRun.outcome as 'succeeded'}`)}
                    </Badge>
                  </DetailRow>
                  <DetailRow label={t('operations.columns.processed')}>
                    {String(job.lastRun.itemsProcessed)}
                  </DetailRow>
                  <DetailRow label={t('operations.columns.failed')}>
                    {String(job.lastRun.itemsFailed)}
                  </DetailRow>
                </>
              ) : null}
            </dl>
          ) : null}
        </DialogBody>
        <DialogFooter className="flex justify-end gap-2">
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            {tCommon('action.close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const JobRunDetailDialog = ({
  run,
  open,
  onOpenChange,
}: {
  run: JobRunRow | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const { t } = useTranslation('admin');
  const { t: tCommon } = useTranslation('common');
  const jobLabel = useJobLabel();

  const detail = useQuery({
    queryKey: ['platform-job-run', run?.id],
    queryFn: () => api.get<JobRunDetail>(`/platform/jobs/runs/${run!.id}`),
    enabled: open && run != null,
  });

  const activeRun = detail.data ?? run;
  const detailsJson = formatJson(detail.data?.details);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        size="md"
        className="overflow-visible sm:max-w-xl"
        closeLabel={tCommon('action.close')}
      >
        <DialogHeader>
          <DialogTitle>
            {activeRun
              ? t('operations.detail.runTitle', { task: jobLabel(activeRun.jobName) })
              : t('operations.detail.runTitleFallback')}
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          {detail.isLoading && !activeRun ? (
            <p className="text-body-sm text-fg-secondary">{t('operations.detail.loading')}</p>
          ) : activeRun ? (
            <dl>
              <DetailRow label={t('operations.columns.startedAt')}>
                {formatDateTime(activeRun.startedAt)}
              </DetailRow>
              <DetailRow label={t('operations.detail.finishedAt')}>
                {activeRun.finishedAt
                  ? formatDateTime(activeRun.finishedAt)
                  : t('operations.detail.stillRunning')}
              </DetailRow>
              <DetailRow label={t('operations.columns.job')}>{jobLabel(activeRun.jobName)}</DetailRow>
              <DetailRow label={t('operations.columns.outcome')}>
                <Badge variant={outcomeBadgeVariant(activeRun.outcome)}>
                  {t(`operations.outcomes.${activeRun.outcome as 'succeeded'}`)}
                </Badge>
              </DetailRow>
              <DetailRow label={t('operations.columns.processed')}>
                {String(activeRun.itemsProcessed)}
              </DetailRow>
              <DetailRow label={t('operations.columns.failed')}>
                {String(activeRun.itemsFailed)}
              </DetailRow>
              <DetailRow label={t('operations.columns.attempt')}>
                {String(activeRun.attempt)}
              </DetailRow>
              {detail.data?.error ? (
                <DetailRow label={t('operations.columns.error')}>
                  <CopyableText
                    value={detail.data.error}
                    copyLabel={t('operations.detail.copyError')}
                    copiedLabel={t('operations.detail.copied')}
                    copyFailedLabel={t('operations.detail.copyFailed')}
                    monospace
                  />
                </DetailRow>
              ) : null}
              {detailsJson ? (
                <DetailRow label={t('operations.detail.details')}>
                  <CopyableText
                    value={detailsJson}
                    copyLabel={t('operations.detail.copyDetails')}
                    copiedLabel={t('operations.detail.copied')}
                    copyFailedLabel={t('operations.detail.copyFailed')}
                    monospace
                  />
                </DetailRow>
              ) : null}
            </dl>
          ) : null}
        </DialogBody>
        <DialogFooter className="flex justify-end gap-2">
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            {tCommon('action.close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const JobDeadLetterDetailDialog = ({
  deadLetter,
  open,
  onOpenChange,
}: {
  deadLetter: DeadLetterRow | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const { t } = useTranslation('admin');
  const { t: tCommon } = useTranslation('common');
  const { t: tErrors } = useTranslation('errors');
  const isAdmin = useIsPlatformAdmin();
  const queryClient = useQueryClient();
  const jobLabel = useJobLabel();
  const [resolveOpen, setResolveOpen] = useState(false);
  const [resolveNote, setResolveNote] = useState('');

  const detail = useQuery({
    queryKey: ['platform-job-dead-letter', deadLetter?.id],
    queryFn: () => api.get<DeadLetterDetail>(`/platform/jobs/dead-letters/${deadLetter!.id}`),
    enabled: open && deadLetter != null,
  });

  const active = detail.data ?? deadLetter;
  const payloadJson = formatJson(detail.data?.payload);
  const errorText = detail.data?.error ?? deadLetter?.error ?? null;
  const unresolved = Boolean(active && !active.resolvedAt);

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ['platform-job-dead-letters'] });
    await queryClient.invalidateQueries({ queryKey: ['platform-job-dead-letter', deadLetter?.id] });
    await queryClient.invalidateQueries({ queryKey: ['platform-operations'] });
    await queryClient.invalidateQueries({ queryKey: ['platform-dashboard'] });
  };

  const retry = useMutation({
    mutationFn: () =>
      api.post<{ outcome: string }>(`/platform/jobs/dead-letters/${deadLetter!.id}/retry`, {}),
    onSuccess: async (result) => {
      const succeeded = result.outcome === 'succeeded' || result.outcome === 'partial';
      toast.success(
        succeeded
          ? t('operations.deadLetters.retried', {
              outcome: t(`operations.outcomes.${result.outcome as 'succeeded'}`),
            })
          : t('operations.deadLetters.stillFailed'),
      );
      await invalidate();
      if (succeeded) onOpenChange(false);
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const resolve = useMutation({
    mutationFn: (note: string) =>
      api.post(`/platform/jobs/dead-letters/${deadLetter!.id}/resolve`, { note }),
    onSuccess: async () => {
      toast.success(t('operations.deadLetters.resolvedToast'));
      setResolveOpen(false);
      setResolveNote('');
      await invalidate();
      onOpenChange(false);
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        size="md"
        className="overflow-visible sm:max-w-xl"
        closeLabel={tCommon('action.close')}
      >
        <DialogHeader>
          <DialogTitle>
            {active
              ? t('operations.detail.deadLetterTitle', { task: jobLabel(active.jobName) })
              : t('operations.detail.deadLetterTitleFallback')}
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          {detail.isLoading && !active ? (
            <p className="text-body-sm text-fg-secondary">{t('operations.detail.loading')}</p>
          ) : active ? (
            <dl>
              <DetailRow label={t('operations.columns.startedAt')}>
                {formatDateTime(active.createdAt)}
              </DetailRow>
              <DetailRow label={t('operations.columns.job')}>{jobLabel(active.jobName)}</DetailRow>
              <DetailRow label={t('operations.columns.church')}>
                {active.churchId ?? '—'}
              </DetailRow>
              <DetailRow label={t('operations.columns.status')}>
                <Badge variant={active.resolvedAt ? 'neutral' : 'danger'}>
                  {active.resolvedAt
                    ? t('operations.deadLetters.resolved')
                    : t('operations.deadLetters.open')}
                </Badge>
              </DetailRow>
              <DetailRow label={t('operations.detail.attempts')}>{String(active.attempts)}</DetailRow>
              {errorText ? (
                <DetailRow label={t('operations.columns.error')}>
                  <CopyableText
                    value={errorText}
                    copyLabel={t('operations.detail.copyError')}
                    copiedLabel={t('operations.detail.copied')}
                    copyFailedLabel={t('operations.detail.copyFailed')}
                    monospace
                  />
                </DetailRow>
              ) : null}
              {payloadJson ? (
                <DetailRow label={t('operations.detail.payload')}>
                  <CopyableText
                    value={payloadJson}
                    copyLabel={t('operations.detail.copyPayload')}
                    copiedLabel={t('operations.detail.copied')}
                    copyFailedLabel={t('operations.detail.copyFailed')}
                    monospace
                  />
                </DetailRow>
              ) : null}
            </dl>
          ) : null}
        </DialogBody>
        <DialogFooter className="flex justify-end gap-2">
          {isAdmin && unresolved ? (
            <>
              <Button variant="outline" loading={retry.isPending} onClick={() => retry.mutate()}>
                {retry.isPending
                  ? t('operations.deadLetters.retrying')
                  : t('operations.deadLetters.retry')}
              </Button>
              <Button variant="outline" onClick={() => setResolveOpen(true)}>
                {t('operations.deadLetters.resolve')}
              </Button>
            </>
          ) : null}
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            {tCommon('action.close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <Dialog
      open={resolveOpen}
      onOpenChange={(next) => {
        if (!next) {
          setResolveOpen(false);
          setResolveNote('');
        }
      }}
    >
      <DialogContent closeLabel={tCommon('action.close')} size="sm">
        <DialogHeader>
          <DialogTitle>{t('operations.deadLetters.resolveTitle')}</DialogTitle>
          <DialogDescription>{t('operations.deadLetters.resolveDescription')}</DialogDescription>
        </DialogHeader>
        <DialogBody className="overflow-visible" contentClassName="flex flex-col gap-4">
          <FormField label={t('operations.deadLetters.resolveNote')} required>
            {(field) => (
              <Textarea
                {...field}
                value={resolveNote}
                onChange={(event) => setResolveNote(event.target.value)}
                placeholder={t('operations.deadLetters.resolveNotePlaceholder')}
              />
            )}
          </FormField>
        </DialogBody>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              setResolveOpen(false);
              setResolveNote('');
            }}
          >
            {tCommon('action.close')}
          </Button>
          <Button
            loading={resolve.isPending}
            disabled={resolveNote.trim().length < 3}
            onClick={() => resolve.mutate(resolveNote.trim())}
          >
            {t('operations.deadLetters.confirmResolve')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
};
